import {
  Column,
  Columns,
  Inline,
  Stack,
  Text,
} from '@lightspeed/design-system-react'
import { useT, T } from '@transifex/react'
import React from 'react'
import { AddressTypes } from '@vend/business-components/dist/components/EditCustomerModal/DetailsTab/Address'
import {
  AddressErrorsStatus,
  AddressErrorStatusTypes,
  getCustomerAddressDetails,
  getFormattedAddress,
} from '../popover/utils/fulfillmentHelper'
import { FulfilmentDetail } from '../../../../../models/fulfilment'
import { useAppSelector } from '../../../../../state/hooks'
import { selectSaleCustomer } from '../../../../../state/slices/currentSale/currentSaleSlice'
import { AddOrEditAddressButton, EditAddressButtons } from './EditAddressButton'
import { useAddressModalHandlers } from './hooks/useAddressModalHandlers'

interface AddressProps {
  isSameBillingAddress: boolean
  formattedShippingAddress: string
  formattedBillingAddress?: string
  isModalOpeningOnError?: boolean
  hasError: boolean
  addressStatus: AddressErrorStatusTypes
  fulfilmentDetail?: FulfilmentDetail
  onSave?: (fulfillmentDetails: FulfilmentDetail[]) => void
  lineItemLocalId?: string
}

export const Addresses: React.FC<AddressProps> = ({
  isSameBillingAddress,
  formattedShippingAddress,
  formattedBillingAddress,
  isModalOpeningOnError,
  hasError,
  addressStatus,
  fulfilmentDetail,
  onSave,
  lineItemLocalId,
}: AddressProps) => {
  const t = useT()
  const shippingAddressTitle = isSameBillingAddress
    ? t('Shipping & billing address', { _context: 'Section title' })
    : t('Shipping address', { _context: 'Section title' })

  const saleCustomer = useAppSelector(selectSaleCustomer)
  const formattedSaleCustomerShippingAddress = saleCustomer
    ? getFormattedAddress(
        getCustomerAddressDetails(saleCustomer, AddressTypes.Shipping)
      )
    : undefined
  const formattedSaleCustomerBillingAddress = saleCustomer
    ? getFormattedAddress(
        getCustomerAddressDetails(saleCustomer, AddressTypes.Billing)
      )
    : undefined
  const isAddressChanged =
    formattedSaleCustomerShippingAddress !== formattedShippingAddress ||
    formattedSaleCustomerBillingAddress !== formattedBillingAddress

  const HasBillingAddressAndNoShippingAddressInModal =
    isModalOpeningOnError &&
    (addressStatus === AddressErrorsStatus.EMPTYSHIPPING_CORRECTBILLING ||
      addressStatus === AddressErrorsStatus.EMPTYSHIPPING_INCORRECTBILLING)
  const billingAddressTitle = t('Billing address', {
    _context: 'Section title',
  })

  const { openAddressModal } = useAddressModalHandlers({
    type: EditAddressButtons.edit,
    fulfilmentDetail,
    onSave,
  })

  return (
    <Stack space={4}>
      <Stack space={3}>
        {HasBillingAddressAndNoShippingAddressInModal ? (
          <>
            <Text type="label">
              <T
                _str="No shipping address added for this customer"
                _context="Modal title"
              />
            </Text>
            <Inline space={0} align="left">
              <div className="vd-flex vd-flex--justify-between vd-flex--align-center">
                <Text type="body">
                  {addressStatus ===
                  AddressErrorsStatus.EMPTYSHIPPING_CORRECTBILLING ? (
                    <T
                      _str="You can use the billing address for shipping or add a new one."
                      _context="Modal description"
                    />
                  ) : (
                    <T
                      _str="Fix billing address errors to use it for shipping. You can also add a new shipping and billing address for this sale."
                      _context="Modal description"
                    />
                  )}
                </Text>
              </div>
            </Inline>
          </>
        ) : (
          <Columns space={1}>
            <Column>
              <Stack space={3}>
                <Text type="label">{shippingAddressTitle}</Text>
                <Text type="body">{formattedShippingAddress}</Text>
              </Stack>
            </Column>
            <Column width="1/12">
              <AddOrEditAddressButton
                type={EditAddressButtons.edit}
                hasError={hasError}
                isModalOpeningOnError={isModalOpeningOnError}
                fulfilmentDetail={fulfilmentDetail}
                onSave={onSave}
                lineItemLocalId={lineItemLocalId}
                onOpenModal={openAddressModal}
              />
            </Column>
          </Columns>
        )}
      </Stack>
      {!isSameBillingAddress && (
        <Stack space={3}>
          <Text type="label">{billingAddressTitle}</Text>
          <Inline space={0} align="left">
            <div className="vd-flex vd-flex--justify-between vd-flex--align-center">
              <Text type="body">{formattedBillingAddress}</Text>
            </div>
          </Inline>
        </Stack>
      )}
      {isAddressChanged && saleCustomer && (
        <Text type="supplementary" className="sale-fulfilment-delivery-hint">
          <T
            _str="Address changes apply to this sale only"
            _context="Note explaining that address updates apply only to a specific sale"
          />
        </Text>
      )}
    </Stack>
  )
}
